<template>
  <div class="app-page">
    <Header ref="header" helpURL="studio/logs/">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
    </Header>
    <div class="main-page-div">
      <main class="main-display-area">
        <div class="logs-page">
          <div class="task-card-wrapper">
            <TaskCard 
              v-if="taskInfo"
              :session="taskInfo"
              :is-list="true"
              :show-icons="false"
              :show-pause="true"
              :is-paused="isPaused"
              @pause-resume="handlePauseResume"
            />
          </div>
          <div class="logs-page__content">
            <LogViewer 
              :task-id="taskId"
              :is-paused="isPaused"
              @update-task-info="taskInfo = $event"
              @update-connection-status="connectionStatus = $event"
            />
          </div>
        </div>
      </main>
    </div>
    <TasksModal />
  </div>
</template>

<script>
import LogViewer from '@/components/Apps/LogViewer.vue';
import TaskCard from '@/components/Apps/TaskCard.vue';
import Header from '@/components/Header.vue';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue';
import TasksModal from '@/components/TasksStatusComponent/TasksModalV2.vue';

export default {
  name: 'LogsPage',
  components: {
    LogViewer,
    TaskCard,
    Header,
    BreadcrumbNavigation,
    TasksModal,
  },
  data() {
    return {
      taskInfo: null,
      connectionStatus: 'disconnected',
      isPaused: false,
    };
  },
  computed: {
    taskId() {
      return this.$route.params.task_id;
    },
  },
  methods: {
    handlePauseResume(isPausedValue) {
      this.isPaused = isPausedValue;
    },
  },
};
</script>

<style lang="scss" scoped>
.logs-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f5f5;

  &__header {
    display: flex;
    align-items: center;
    align-content: center;
    gap: 12px;
    padding: 12px 24px;
    background: white;
    border-bottom: 1px solid #e0e0e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    flex-wrap: wrap;
    min-height: fit-content;
  }

  &__back-link {
    display: inline-flex;
    align-items: center;
    padding: 6px 10px;
    border-radius: 4px;
    color: #0066cc;
    text-decoration: none;
    transition: background-color 0.2s ease;
    flex-shrink: 0;
    margin-top: 2px;
    border: none;
    background: none;
    font-size: 14px;
    cursor: pointer;
    font-family: inherit;

    &:hover {
      background-color: #f0f0f0;
    }

    &:active {
      background-color: #e8e8e8;
    }
  }

  &__header-content {
    display: flex;
    align-items: center;
    align-content: center;
    gap: 12px;
    flex: 1;
    flex-wrap: wrap;
  }

  &__task-id {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 10px;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 12px;
  }

  &__task-name {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 10px;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 12px;
  }

  &__status-badge {
    display: inline-flex;
    align-items: center;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;

    &.status-complete {
      background: #d4edda;
      color: #155724;
    }

    &.status-error {
      background: #f8d7da;
      color: #721c24;
    }

    &.status-running {
      background: #cfe2ff;
      color: #084298;
    }

    &.status-unknown {
      background: #e2e3e5;
      color: #383d41;
    }
  }

  &__progress-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: auto;
    margin: auto 0;
  }

  &__message-row {
    display: flex;
    gap: 8px;
    padding: 5px 10px;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 12px;
    max-width: 400px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__message-text {
    color: #666;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__connection-status {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    background: #f5f5f5;
    border-radius: 4px;
    font-size: 12px;

    &.connecting {
      .logs-page__status-indicator {
        background: #ffc107;
        animation: pulse 1.5s infinite;
      }
    }

    &.connected {
      .logs-page__status-indicator {
        background: #28a745;
      }
    }

    &.disconnected {
      .logs-page__status-indicator {
        background: #6c757d;
      }
    }

    &.error {
      .logs-page__status-indicator {
        background: #dc3545;
      }
    }
  }

  &__status-indicator {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &__status-text {
    color: #666;
    font-weight: 600;
    font-size: 11px;
  }

  &__parent-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
  }

  &__parent-link {
    color: #0066cc;
    text-decoration: none;
    cursor: pointer;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }

  &__content {
    flex: 1;
    padding: 16px 24px;
    overflow: hidden;
  }

  &__label {
    color: #666;
    font-weight: 600;
  }

  &__value {
    color: #0066cc;
    font-family: 'Courier New', monospace;
    font-weight: 500;
  }
}

.task-card-wrapper {
  margin: 0 20px;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
